import Box from 'UIKit/Box/Box'
import Button from 'UIKit/Button'
import Dots from 'UIKit/Loader/Dots'
import Text from 'UIKit/Text'
import useDebounceCallback from 'hooks/useDebounceCallback'
import { useRequest } from 'hooks/useRequest'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BaseResponse, HunnyRequest, Paging } from 'services/types'
import styled from 'styled-components'
import { delayed } from 'utils'
import { forkjoinRequest } from 'utils/requestHelper'
import ListStyleUpdater from './ListStyleUpdater'

interface HunnyListProps {
  Item: React.FC<{ item: any }>
  Loader: React.ReactNode
  limit: number
  request?: (offset: number, limit: number) => HunnyRequest<BaseResponse<Paging<any>>>
  uniqueKey?: string
  noContent?: any
  onCountChanged?: (count: number) => void
  processContent?: string
  delay?: number
  listName?: string
}

const HunnyList: React.FC<HunnyListProps> = ({
  Item,
  request,
  Loader,
  limit = 24,
  processContent = "You've viewed {{count}} of {{total}} items",
  uniqueKey = 'id',
  delay = 200,
  onCountChanged,
  noContent,
  listName = 'item-list',
}) => {
  const [pages, setPages] = useState([])
  const [total, setTotal] = useState(0)
  const [nextPage, setNextPage] = useState([])
  const [isTriggerLoad, setIsTriggerLoad] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { execute, cancelAllRequest } = useRequest()
  const debouceCallback = useDebounceCallback()
  const [isFetched, setIsFetched] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    cancelAllRequest()
    setPages([])
    setNextPage([])

    const fetch = async (offset: number, limit: number) => {
      let response: BaseResponse<Paging<any>>

      if (typeof request === 'function') {
        const [res] = await forkjoinRequest([execute(request(offset, limit)), delayed(delay)])
        response = res
      }

      if (response?.data && response.data.items.length) {
        setTotal(response.data.paging?.total || 0)
        return response.data.items
      }
      return null
    }

    const initialData = async () => {
      setIsLoading(true)
      const firstPageItems = await fetch(0, limit)
      setIsFetched(true)

      if (firstPageItems?.length > 0) {
        setPages([firstPageItems])
        if (firstPageItems.length === limit) {
          const nextPageItems = await fetch(limit, limit)
          setNextPage(nextPageItems || [])
        }

        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }

    initialData()
  }, [request])

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)
      let response: BaseResponse<Paging<any>>
      if (typeof request === 'function') {
        setPages([...pages, nextPage])
        const offset = (pages.length + 1) * limit
        const [res] = await forkjoinRequest([execute(request(offset, limit))])
        response = res
      }
      if (response?.data && response.data.items.length) {
        setNextPage(response.data.items)
      } else {
        setNextPage([])
      }
      setIsLoading(false)
    }
    if (isTriggerLoad !== null) fetch()
  }, [isTriggerLoad])

  const count = ((pages.length || 1) - 1) * limit + (pages[pages.length - 1]?.length || 0)

  useEffect(() => {
    if (!isLoading) debouceCallback(() => onCountChanged && onCountChanged(count), 300)
  }, [count, isLoading])

  const isNoContent = !isLoading && !pages.length && noContent && isFetched

  return (
    <>
      <ListStyleUpdater
        isDisplayShowMore={nextPage.length > 0}
        isDisplayProgress={total > limit}
        isNoContent={isNoContent}
      />
      {isNoContent && (
        <Text color="textSubtle" className="list-no-content">
          {noContent}
        </Text>
      )}

      {pages?.map((pageData, index) => (
        <ItemList key={`${listName}-${index}`} data={pageData} Item={Item} uniqueKey={uniqueKey} />
      ))}

      {isLoading && !pages.length && <>{Loader}</>}

      {pages.length > 0 && (
        <>
          {total > limit && (
            <Box display="absolute" className="hunny-list-process" maxWidth="360px" width="calc(100% - 24px)">
              <Text fontSize="12px" width="100%" textAlign="center" color="textSubtle" mb="6px">
                {t(processContent, {
                  count,
                  total,
                })}
              </Text>
              <StyledProcess width="100%" height="2px" background="red" $process={count / total} />
            </Box>
          )}

          {nextPage.length > 0 && (
            <Button
              maxWidth="192px"
              width="100%"
              className="hunny-list-loadmore-btn"
              variant="secondary"
              style={{ background: 'rgba(255,255,255,0.05)' }}
              onClick={() => setIsTriggerLoad({})}
              disabled={isLoading}
            >
              {isLoading ? (
                <Text>
                  <Dots>
                    <Trans>Loading</Trans>
                  </Dots>
                </Text>
              ) : (
                <Text fontSize={['12px', '', '14px']}>
                  <Trans>Show more</Trans>
                </Text>
              )}
            </Button>
          )}
        </>
      )}
    </>
  )
}

const ItemList = React.memo(
  ({ data, Item, uniqueKey }: { data: any[]; Item: React.FC<{ item: any }>; uniqueKey: string }) => {
    return (
      <>
        {data.map((item) => (
          <Item item={item} key={item[uniqueKey]} />
        ))}
      </>
    )
  },
)

const StyledProcess = styled(Box)<{ $process: number }>`
  background: ${({ theme }) => theme.colors.buttonBackgroundAlt2};
  position: relative;
  border-radius: 4px;

  &:after {
    content: '';
    position: absolute;
    top: 0px;
    left: 0px;
    width: ${({ $process }) => $process * 100}%;
    height: 100%;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.primary};
  }
`

export default HunnyList
